import type { FieldChangeEvent } from "~business/Cards/types";
import React from "react";
import DetalleCard from "~business/Cards/DetalleCard";
import { useDispatch, useSelector } from "react-redux";
import PersonIcon from "@mui/icons-material/Person";
import ItemInputCard from "~business/Cards/ItemInputCard";

import {
  setMail,
  setTelefono,
  setNumeroDocumentoAsegurado,
  setNombreAsegurado,
  setSubmitting,
} from "../actions";
import { toPascalCase } from "~libs/stringsPascalCase";
import { isDni, isEmail } from "~libs/validators";
import { CampoSiniestro, PolizaVinculada } from "../../types";

interface SliceDatosAsegurado {
  numeroDocumentoAsegurado: CampoSiniestro;
  nombreAsegurado: CampoSiniestro;
  mail: CampoSiniestro;
  telefono: CampoSiniestro;
  poliza: PolizaVinculada | null;
}

const useReduxSelector = () =>
  useSelector<any, SliceDatosAsegurado>((state) => ({
    numeroDocumentoAsegurado:
      state.cargaSiniestroPublicaReducer.numeroDocumentoAseguradoSelected,
    nombreAsegurado: state.cargaSiniestroPublicaReducer.nombreAseguradoSelected,
    mail: state.cargaSiniestroPublicaReducer.mailSelected,
    telefono: state.cargaSiniestroPublicaReducer.telefonoSelected,
    poliza: state.cargaSiniestroPublicaReducer.polizaVinculada,
  }));

const title = "Datos del Asegurado";

interface DatosAseguradoCardProps {
  isLoading?: boolean;
}

const DatosAseguradoCard = ({ isLoading }: DatosAseguradoCardProps) => {
  const dispatch = useDispatch();

  const { numeroDocumentoAsegurado, nombreAsegurado, mail, telefono, poliza } =
    useReduxSelector();

  let timer: ReturnType<typeof setTimeout> | undefined;

  const handleChangeNumeroDocumento = (
    numeroDocumento: FieldChangeEvent<string | null>
  ) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setNumeroDocumentoAsegurado(numeroDocumento));
    }, 200);
  };

  const handleChangeNombre = (nombre: FieldChangeEvent<string | null>) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setNombreAsegurado(nombre));
    }, 200);
  };

  const handleChangeMail = (mail: FieldChangeEvent<string | null>) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setMail(mail));
    }, 200);
  };

  const handleChangeTelefono = (telefono: FieldChangeEvent<string | null>) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      dispatch(setTelefono(telefono));
    }, 200);
  };

  const handleBlur = () => {
    dispatch(setSubmitting(false));
  };

  return (
    <DetalleCard title={title} IconHeader={PersonIcon}>
      {!poliza && (
        <>
          <ItemInputCard
            id={"dni"}
            isLoading={isLoading}
            label={"DNI"}
            value={""}
            onChange={handleChangeNumeroDocumento}
            onBlur={handleBlur}
            focus={numeroDocumentoAsegurado.focus}
            required
            numbersOnly
            customValidate={isDni}
            placeholder="Ej: 12345678"
          />
          <ItemInputCard
            id={"nombreAsegurado"}
            isLoading={isLoading}
            label={"Nombre Completo"}
            value={""}
            onChange={handleChangeNombre}
            onBlur={handleBlur}
            focus={nombreAsegurado.focus}
            required
            formatValue={toPascalCase}
          />
          <ItemInputCard
            id={"mail"}
            isLoading={isLoading}
            label={"Correo Electrónico"}
            value={mail.value}
            onChange={handleChangeMail}
            focus={mail.focus}
            required
            customValidate={isEmail}
            requiredText="Ingrese un email válido"
            invalidText="Ingrese un email válido"
          />
          <ItemInputCard
            id={"telefono"}
            isLoading={isLoading}
            label={"Teléfono"}
            value={telefono.value}
            onChange={handleChangeTelefono}
            focus={telefono.focus}
            required
          />
        </>
      )}
      {poliza && (
        <>
          <ItemInputCard
            id={"dni"}
            isLoading={isLoading}
            label={"DNI"}
            value={numeroDocumentoAsegurado.value}
            readOnly
          />
          <ItemInputCard
            id={"nombreAsegurado"}
            isLoading={isLoading}
            label={"Nombre Completo"}
            value={nombreAsegurado.value}
            readOnly
          />
          <ItemInputCard
            id={"mail"}
            isLoading={isLoading}
            label={"Correo Electrónico"}
            value={mail.value}
            onChange={handleChangeMail}
            focus={mail.focus}
            required
          />
          <ItemInputCard
            id={"telefono"}
            isLoading={isLoading}
            label={"Teléfono"}
            value={telefono.value}
            onChange={handleChangeTelefono}
            focus={telefono.focus}
            required
          />
        </>
      )}
    </DetalleCard>
  );
};

export default DatosAseguradoCard;
